'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { detectRegion, createConsentRecord, saveConsentRecord, logConsentAction, getConsentRecord } from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<'gdpr' | 'ccpa' | 'none'>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    // Only show banner if no consent record exists
    const existingConsent = getConsentRecord();
    if (!existingConsent && detectedRegion !== 'none') {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsentRecord(record);
    logConsentAction('accept_all', record);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    saveConsentRecord(record);
    logConsentAction('reject_all', record);
    setShowBanner(false);
  };

  const handleCCPADismiss = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: false,
      preferences: true,
    });
    saveConsentRecord(record);
    logConsentAction('ccpa_dismiss', record);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  // GDPR Banner
  if (region === 'gdpr') {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#2F1B14] via-[#3D2318] to-[#2F1B14] border-t border-[#8B4513]/40 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <h2 className="text-lg font-bold text-[#F4E8D0] mb-2">Your Privacy Matters</h2>
                <p className="text-sm text-[#E8DCC8] mb-3">
                  We use cookies to enhance your experience, analyze site usage, and support our marketing efforts. 
                  <Link href="/privacy" className="text-[#D2691E] hover:text-[#FF6B35] underline ml-1">
                    Learn more
                  </Link>
                  {' '}or{' '}
                  <Link href="/cookies" className="text-[#D2691E] hover:text-[#FF6B35] underline">
                    view our cookie policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:gap-2 flex-shrink-0">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-2.5 text-sm font-semibold text-[#E8DCC8] bg-[#2F1B14] hover:bg-[#3D2318] rounded-md transition-colors duration-200 border border-[#8B4513]/50 hover:border-[#8B4513]"
                >
                  Reject All
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-6 py-2.5 text-sm font-semibold text-[#2F1B14] bg-[#E8DCC8] hover:bg-[#F4E8D0] rounded-md transition-colors duration-200 border border-[#8B4513]/40"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-[#8B4513] hover:bg-[#D2691E] rounded-md transition-colors duration-200 border border-[#8B4513]"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
        {showPreferences && (
          <ConsentPreferenceCenter
            isOpen={showPreferences}
            onClose={() => setShowPreferences(false)}
            onSave={(choices) => {
              const record = createConsentRecord(region, choices);
              saveConsentRecord(record);
              logConsentAction('save_preferences', record);
              setShowBanner(false);
            }}
          />
        )}
      </>
    );
  }

  // CCPA Banner
  if (region === 'ccpa') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#2F1B14] via-[#3D2318] to-[#2F1B14] border-t border-[#8B4513]/40 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-[#E8DCC8]">
              We respect your privacy. California residents can{' '}
              <Link href="/privacy#do-not-sell" className="text-[#D2691E] hover:text-[#FF6B35] underline font-semibold">
                opt out of the sale of personal information
              </Link>
              .
            </p>
            <button
              onClick={handleCCPADismiss}
              className="px-6 py-2 text-sm font-semibold text-[#F4E8D0] bg-[#8B4513] hover:bg-[#D2691E] rounded-md transition-colors duration-200 border border-[#8B4513] whitespace-nowrap"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
